/**
 * Launch an ADVICE `memo_compose` session — the "Create a partner note" front
 * door (compose-note variant). Unlike the receiver-side `memo_entry` edge, this
 * starts an intent-first session whose whole purpose is to reach a shareable
 * understanding worth sending; there is no received note and no memoId.
 *
 * Entry-launch sequence contract: swap to the chat surface, switch to ADVICE
 * with `entryType: 'memo_compose'`, and auto-send a FIXED placeholder opener.
 * The opener is an intent signal, NOT compose-consent — the backend gates the
 * composeMemo tool (and the offer/pill) to the depth floor, so it cannot fire a
 * note on turn 1. The first AdviceStrategy turn skips SCOPING/ATTUNEMENT into
 * PATH_WORK/Explore and opens with the mini-scope.
 *
 * Used by `PartnerNotesSection` (the "New note" filter-bar action).
 *
 * @returns {Promise<boolean>} true if the session launched, false on a guarded bail.
 */

// Module-scoped in-flight guard (R2-6): a double-click must not spawn two
// overlapping `force:true` sessions — the filter-bar action isn't disabled while
// async and switchMode has no mutex of its own.
let launchInFlight = false;

export async function launchComposeNoteAdvice() {
  // Couple re-check (R2-5): belt-and-braces with the /notes page couple-gate.
  // Mirrors PartnerNotesSection.activate()'s gate on the same client field.
  if (!window.app?.currentUser?.coupleId) {
    console.warn('[composeNoteLauncher] not coupled — cannot launch memo_compose advice');
    window.notificationManager?.showError('Connect with your partner to create a note.');
    return false;
  }

  if (launchInFlight) return false;

  const orchestrator = window.app?.modules?.modeOrchestrator;
  if (!orchestrator) {
    console.error('[composeNoteLauncher] modeOrchestrator unavailable');
    window.notificationManager?.showError('Could not open the conversation. Please try again.');
    return false;
  }

  launchInFlight = true;
  try {
    // Await the page swap and bail if PageManager superseded us — running
    // switchMode while the chat surface isn't visible would create an ADVICE
    // session the user never sees. (Not an error — a benign supersede.)
    const swapped = await window.app?.modules?.pageManager?.showPage('chat');
    if (swapped === false) return false;
    window.history.pushState({}, '', '/chat');

    // `force: true` is REQUIRED: the "New note" click can fire while the user is
    // ALREADY in an ADVICE session (mode persists across page navigation).
    // Without force, switchMode's same-mode early-return no-ops the switch and
    // the opener below strands in the prior session. force creates a fresh
    // memo_compose session (backend closes the prior one).
    await orchestrator.switchMode('ADVICE', {
      entryType: 'memo_compose',
      skipWelcome: true,
      force: true
    });
    // FIXED placeholder opener — an intent signal, not compose-consent (see the
    // OPENING_GUIDANCE[memo_compose] register). The backend's depth floor keeps
    // the note off turn 1.
    await orchestrator.handleUserMessage('I want to compose a partner note.', {
      metadata: { source: 'memo_compose' }
    });
    return true;
  } catch (err) {
    // Distinguish the opener-delivery failure from the benign page-swap supersede
    // above: only a genuine switchMode/handleUserMessage throw surfaces an error.
    console.error('[composeNoteLauncher] launch failed:', err);
    window.notificationManager?.showError('Could not open the conversation. Please try again.');
    return false;
  } finally {
    launchInFlight = false;
  }
}
